/**
 * ModelSelect — catalog-native unified model selector.
 *
 * Replaces the legacy Inline/Compact/Enhanced/Modal four-state ModelSelector
 * families with a single component that reads CatalogModel directly (no
 * legacy adapter flattening). Composes model-display primitives for rendering;
 * never branches on modality/billing inline.
 *
 * Long lists are tamed by three affordances (carried over from the legacy
 * selector's service capabilities): a bounded-height scroll body, routing-tier
 * grouping with collapsible headers, and a tag multi-select filter — on top of
 * the existing text search. All chrome is i18n-keyed.
 *
 * Variants:
 *  - 'inline'  (default): full listbox, always visible
 *  - 'compact':           denser listbox, always visible
 *  - 'modal':             trigger button opens a dialog list
 */

import { useMemo, useState, useId } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { ChevronDown, ChevronRight, X } from 'lucide-react'
import { cn } from '@/lib/cn'
import { useI18n } from '@/i18n'
import { useCatalogModels } from '@/lib/queries/useCatalogModels'
import { modelId, displayName, routingTier, organization, supportsVision, supportsReasoning, supportsFunctionCalling, sameModelId } from '@/lib/models/catalogAccessors'
import { ModelBadge } from './ModelBadge'
import { BillingLabel } from './BillingLabel'
import { ModelCapabilitySummary } from './ModelCapabilitySummary'
import type { CatalogModel, Modality } from '@/types'

type ImageOperation = 'generate' | 'edit' | 'fuse'

// Tier group ordering; unknown tiers and the 'other' bucket sort after these.
const TIER_ORDER = ['official', 'stable', 'dev', 'bltcy']
const KNOWN_TIERS = new Set(TIER_ORDER)
const OTHER_GROUP = 'other'
// Above this count (and with >1 group), groups start collapsed so a 200+ model
// list reads as a short stack of tier headers rather than an endless scroll.
const COLLAPSE_THRESHOLD = 40

// ---------------------------------------------------------------------------
// cva variants (typed via VariantProps — DS rule §3)
// ---------------------------------------------------------------------------

const modelSelectVariants = cva(
  'relative w-full border border-border bg-card text-foreground rounded-lg overflow-hidden',
  {
    variants: {
      variant: {
        inline: 'shadow-elevation-1',
        compact: '',
        modal: '',
      },
    },
    defaultVariants: { variant: 'inline' },
  }
)

const optionVariants = cva(
  'flex flex-col gap-1 cursor-pointer select-none rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-ring',
  {
    variants: {
      variant: {
        inline: 'px-3 py-3',
        compact: 'px-2 py-1.5',
        modal: 'px-3 py-3',
      },
      selected: {
        true: 'bg-primary/10 text-primary',
        false: 'hover:bg-muted',
      },
    },
    defaultVariants: { variant: 'inline', selected: false },
  }
)

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ModelSelectProps extends VariantProps<typeof modelSelectVariants> {
  value: string
  onChange: (modelId: string) => void
  modality?: Modality
  tier?: string
  operationFilter?: ImageOperation
  /**
   * Further narrows text-modality results by capability sub-filter.
   * Only meaningful when modality='text'. Uses supportsVision / supportsReasoning
   * / supportsFunctionCalling accessors so operators cannot pick a
   * capability-incapable model into a slot that requires that capability.
   */
  capabilityFilter?: 'vision' | 'reasoning' | 'function_calling'
  /** Allow clearing the selection (selecting no model). */
  allowEmpty?: boolean
  /** Label shown in the trigger and as the empty option row when allowEmpty is true. */
  emptyLabel?: string
  disabled?: boolean
  className?: string
}

// ---------------------------------------------------------------------------
// Grouping helpers
// ---------------------------------------------------------------------------

interface ModelGroup {
  key: string
  models: CatalogModel[]
}

function groupKeyOf(model: CatalogModel): string {
  // routingTier() returns '' (not null) for untiered models — use || so the
  // empty string falls through to the 'other' bucket (a blank header otherwise).
  return routingTier(model) || OTHER_GROUP
}

function groupModels(models: CatalogModel[]): ModelGroup[] {
  const buckets = new Map<string, CatalogModel[]>()
  for (const m of models) {
    const key = groupKeyOf(m)
    const arr = buckets.get(key)
    if (arr) arr.push(m)
    else buckets.set(key, [m])
  }
  const rank = (key: string): number => {
    const i = TIER_ORDER.indexOf(key)
    if (i !== -1) return i
    if (key === OTHER_GROUP) return TIER_ORDER.length + 1
    return TIER_ORDER.length // unknown tiers between known tiers and 'other'
  }
  return Array.from(buckets.entries())
    .map(([key, ms]) => ({ key, models: ms }))
    .sort((a, b) => rank(a.key) - rank(b.key) || a.key.localeCompare(b.key))
}

// ---------------------------------------------------------------------------
// Search box sub-component
// ---------------------------------------------------------------------------

interface SearchBoxProps {
  value: string
  onChange: (v: string) => void
  placeholder: string
  className?: string
}

function SearchBox({ value, onChange, placeholder, className }: SearchBoxProps) {
  return (
    <div className={cn('px-2 py-2', className)}>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className={cn(
          'w-full rounded-md border border-input bg-card text-foreground text-sm px-2 py-1',
          'placeholder:text-muted-foreground',
          'focus:outline-none focus:ring-2 focus:ring-ring',
        )}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tag filter chips
// ---------------------------------------------------------------------------

interface TagFilterProps {
  tags: string[]
  selected: Set<string>
  onToggle: (tag: string) => void
  onClear: () => void
  label: string
  clearLabel: string
}

function TagFilter({ tags, selected, onToggle, onClear, label, clearLabel }: TagFilterProps) {
  if (tags.length === 0) return null
  return (
    <div className="px-2 py-2 flex flex-wrap items-center gap-1.5">
      <span className="text-xs text-muted-foreground mr-0.5">{label}</span>
      {tags.map((tag) => {
        const active = selected.has(tag)
        return (
          <button
            key={tag}
            type="button"
            aria-pressed={active}
            onClick={() => onToggle(tag)}
            className={cn(
              'text-xs rounded-full px-2 py-0.5 border transition-colors focus:outline-none focus:ring-2 focus:ring-ring',
              active
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-muted text-muted-foreground border-border hover:bg-accent',
            )}
          >
            {tag}
          </button>
        )
      })}
      {selected.size > 0 && (
        <button
          type="button"
          onClick={onClear}
          className="text-xs text-muted-foreground hover:text-foreground underline focus:outline-none focus:ring-2 focus:ring-ring rounded"
        >
          {clearLabel}
        </button>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Empty option row (when allowEmpty=true)
// ---------------------------------------------------------------------------

interface EmptyOptionRowProps {
  isSelected: boolean
  variant: 'inline' | 'compact' | 'modal'
  emptyLabel: string
  onSelect: () => void
}

function EmptyOptionRow({ isSelected, variant, emptyLabel, onSelect }: EmptyOptionRowProps) {
  return (
    <div
      role="option"
      aria-selected={isSelected}
      aria-label="clear selection"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onSelect()
        }
      }}
      className={cn(optionVariants({ variant, selected: isSelected }))}
    >
      <span className={cn('text-sm italic', isSelected ? 'text-primary' : 'text-muted-foreground')}>
        {emptyLabel}
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Option row
// ---------------------------------------------------------------------------

interface OptionRowProps {
  model: CatalogModel
  isSelected: boolean
  variant: 'inline' | 'compact' | 'modal'
  showCapability: boolean
  onClick: () => void
}

function OptionRow({ model, isSelected, variant, showCapability, onClick }: OptionRowProps) {
  const id = modelId(model)
  const name = displayName(model)

  return (
    <div
      role="option"
      aria-selected={isSelected}
      aria-label={id}
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
      className={cn(optionVariants({ variant, selected: isSelected }))}
    >
      <div className="flex items-center justify-between gap-2 min-w-0">
        <span className={cn('font-medium text-sm truncate', isSelected ? 'text-primary' : 'text-foreground')}>
          {name}
        </span>
        <BillingLabel billing={model.billing} className="shrink-0 text-xs" />
      </div>
      <ModelBadge model={model} />
      {showCapability && isSelected && (
        <ModelCapabilitySummary capability={model.capability} />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Group header
// ---------------------------------------------------------------------------

interface GroupHeaderProps {
  label: string
  count: number
  open: boolean
  onToggle: () => void
}

function GroupHeader({ label, count, open, onToggle }: GroupHeaderProps) {
  return (
    <button
      type="button"
      aria-expanded={open}
      onClick={onToggle}
      className={cn(
        'w-full flex items-center gap-1.5 px-2 py-1.5 bg-muted/50 text-xs font-semibold text-muted-foreground',
        'hover:bg-muted transition-colors focus:outline-none focus:ring-2 focus:ring-ring',
      )}
    >
      {open ? <ChevronDown size={13} aria-hidden /> : <ChevronRight size={13} aria-hidden />}
      <span className="uppercase tracking-wide">{label}</span>
      <span className="ml-auto font-normal">{count}</span>
    </button>
  )
}

// ---------------------------------------------------------------------------
// Grouped option list
// ---------------------------------------------------------------------------

interface OptionListProps {
  models: CatalogModel[]
  value: string
  variant: 'inline' | 'compact' | 'modal'
  onSelect: (id: string) => void
  isGroupOpen: (key: string) => boolean
  onToggleGroup: (key: string) => void
  tierLabel: (key: string) => string
  noModelsLabel: string
  allowEmpty?: boolean
  emptyLabel?: string
}

function OptionList({
  models,
  value,
  variant,
  onSelect,
  isGroupOpen,
  onToggleGroup,
  tierLabel,
  noModelsLabel,
  allowEmpty,
  emptyLabel,
}: OptionListProps) {
  if (models.length === 0 && !allowEmpty) {
    return (
      <div className="px-3 py-4 text-sm text-muted-foreground text-center">
        {noModelsLabel}
      </div>
    )
  }

  const groups = groupModels(models)
  const showHeaders = groups.length > 1

  return (
    <div role="listbox" className="flex flex-col">
      {allowEmpty && (
        <div className="divide-y divide-border">
          <EmptyOptionRow
            isSelected={value === ''}
            variant={variant}
            emptyLabel={emptyLabel ?? 'Use system default'}
            onSelect={() => onSelect('')}
          />
        </div>
      )}
      {groups.map((group) => {
        const open = !showHeaders || isGroupOpen(group.key)
        return (
          <div key={group.key} role="group" aria-label={tierLabel(group.key)}>
            {showHeaders && (
              <GroupHeader
                label={tierLabel(group.key)}
                count={group.models.length}
                open={open}
                onToggle={() => onToggleGroup(group.key)}
              />
            )}
            {open && (
              <div className="flex flex-col divide-y divide-border">
                {group.models.map((m) => {
                  const id = modelId(m)
                  return (
                    <OptionRow
                      key={id}
                      model={m}
                      isSelected={sameModelId(value, id)}
                      variant={variant}
                      showCapability={variant !== 'compact'}
                      onClick={() => onSelect(id)}
                    />
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Selected summary (the "locked" view shared by all variants)
// ---------------------------------------------------------------------------

interface SelectedSummaryProps {
  model: CatalogModel | undefined
  isEmptySelected: boolean
  ariaLabel: string
  emptyLabel: string
  selectLabel: string
  reselectLabel: string
  disabled?: boolean
  open: boolean
  ariaHasPopup: 'dialog' | 'listbox'
  onOpen: () => void
  className?: string
}

function SelectedSummary({
  model,
  isEmptySelected,
  ariaLabel,
  emptyLabel,
  selectLabel,
  reselectLabel,
  disabled,
  open,
  ariaHasPopup,
  onOpen,
  className,
}: SelectedSummaryProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onOpen}
      aria-label={ariaLabel}
      aria-haspopup={ariaHasPopup}
      aria-expanded={open}
      className={cn(
        'w-full flex items-center gap-3 border border-border bg-card text-foreground rounded-lg px-3 py-2.5 text-sm text-left transition-colors',
        'hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring',
        'disabled:opacity-60 disabled:cursor-not-allowed',
        className,
      )}
    >
      {model ? (
        <span className="flex-1 min-w-0 flex flex-col gap-1">
          <span className="flex items-center justify-between gap-2 min-w-0">
            <span className="font-medium truncate">{displayName(model)}</span>
            <BillingLabel billing={model.billing} className="shrink-0 text-xs" />
          </span>
          <ModelBadge model={model} />
        </span>
      ) : isEmptySelected ? (
        <span className="flex-1 text-muted-foreground italic">{emptyLabel}</span>
      ) : (
        <span className="flex-1 text-muted-foreground">{selectLabel}</span>
      )}
      {(model || isEmptySelected) && (
        <span className="shrink-0 text-xs text-primary font-medium">{reselectLabel}</span>
      )}
      <ChevronDown size={14} className="text-muted-foreground shrink-0" aria-hidden />
    </button>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function ModelSelect({
  value,
  onChange,
  modality,
  tier,
  operationFilter,
  capabilityFilter,
  allowEmpty = false,
  emptyLabel,
  disabled = false,
  variant = 'inline',
  className,
}: ModelSelectProps) {
  const { t } = useI18n()
  // Picker open by default only for an inline required-but-missing selection.
  const [open, setOpen] = useState(() => variant !== 'modal' && value === '' && !allowEmpty && !disabled)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set())
  const [selectedVendors, setSelectedVendors] = useState<Set<string>>(new Set())
  // Explicit per-group open/closed overrides (true=open). Absent → default rule.
  const [groupOverride, setGroupOverride] = useState<Record<string, boolean>>({})
  const labelId = useId()

  const resolvedEmptyLabel = emptyLabel ?? t('modelSelect.useSystemDefault')
  const tierLabel = (key: string): string =>
    KNOWN_TIERS.has(key) || key === OTHER_GROUP ? t(`modelSelect.tier.${key}`) : key

  const { data: models = [], isLoading, error } = useCatalogModels(modality)

  const afterTier = tier ? models.filter((m) => routingTier(m) === tier) : models

  const afterOpFilter = operationFilter
    ? afterTier.filter((m) => {
        if (m.capability.modality !== 'image') return false
        return m.capability.operations.includes(operationFilter)
      })
    : afterTier

  const afterCapFilter = capabilityFilter
    ? afterOpFilter.filter((m) => {
        if (capabilityFilter === 'vision') return supportsVision(m)
        if (capabilityFilter === 'reasoning') return supportsReasoning(m)
        return supportsFunctionCalling(m)
      })
    : afterOpFilter

  const allVendors = useMemo(() => {
    const set = new Set<string>()
    for (const m of afterCapFilter) {
      const org = organization(m)
      if (org) set.add(org)
    }
    return Array.from(set).sort()
  }, [afterCapFilter])

  const afterVendors = selectedVendors.size
    ? afterCapFilter.filter((m) => selectedVendors.has(organization(m)))
    : afterCapFilter

  const allTags = useMemo(() => {
    const set = new Set<string>()
    for (const m of afterCapFilter) for (const tag of m.tags ?? []) set.add(tag)
    return Array.from(set).sort()
  }, [afterCapFilter])

  const afterTags = selectedTags.size
    ? afterVendors.filter((m) => {
        const tags = new Set(m.tags ?? [])
        for (const want of selectedTags) if (!tags.has(want)) return false
        return true
      })
    : afterVendors

  const q = searchQuery.trim().toLowerCase()
  const filtered = q
    ? afterTags.filter((m) =>
        modelId(m).toLowerCase().includes(q) ||
        displayName(m).toLowerCase().includes(q),
      )
    : afterTags

  const filterActive = q.length > 0 || selectedTags.size > 0 || selectedVendors.size > 0
  const groups = groupModels(filtered)
  const selectedModelForGroup = value ? filtered.find((m) => sameModelId(modelId(m), value)) : undefined
  const selectedGroupKey = selectedModelForGroup ? groupKeyOf(selectedModelForGroup) : undefined
  const autoCollapse = !filterActive && filtered.length > COLLAPSE_THRESHOLD && groups.length > 1
  // When auto-collapsing with nothing selected, keep the first group open so the
  // list never opens looking empty (just a stack of headers).
  const defaultOpenKey = selectedGroupKey ?? groups[0]?.key

  function isGroupOpen(key: string): boolean {
    if (filterActive) return true
    if (key in groupOverride) return groupOverride[key]
    return !autoCollapse || key === defaultOpenKey
  }

  function toggleGroup(key: string) {
    setGroupOverride((prev) => ({ ...prev, [key]: !isGroupOpen(key) }))
  }

  function toggleTag(tag: string) {
    setSelectedTags((prev) => {
      const next = new Set(prev)
      if (next.has(tag)) next.delete(tag)
      else next.add(tag)
      return next
    })
  }

  function toggleVendor(vendor: string) {
    setSelectedVendors((prev) => {
      const next = new Set(prev)
      if (next.has(vendor)) next.delete(vendor)
      else next.add(vendor)
      return next
    })
  }

  // Resolve the locked summary against the full modality base, NOT a narrowed
  // set — search/tag/vendor AND the capability/operation facets only constrain
  // the pickable list; they must never hide the actual stored selection (a
  // reasoning slot storing a non-reasoning model must still show it, not read as
  // unselected). sameModelId aligns a stored value past a missing/legacy tier prefix.
  const selectedModel = models.find((m) => sameModelId(modelId(m), value))

  function handleSelect(id: string) {
    if (!disabled) {
      onChange(id)
      setSearchQuery('')
      setOpen(false)
    }
  }

  const listBody = (
    <OptionList
      models={filtered}
      value={value}
      variant={variant === 'modal' ? 'modal' : (variant ?? 'inline')}
      onSelect={handleSelect}
      isGroupOpen={isGroupOpen}
      onToggleGroup={toggleGroup}
      tierLabel={tierLabel}
      noModelsLabel={t('modelSelect.noModels')}
      allowEmpty={allowEmpty}
      emptyLabel={resolvedEmptyLabel}
    />
  )

  const controls = (
    <>
      {allVendors.length > 1 && (
        <TagFilter
          tags={allVendors}
          selected={selectedVendors}
          onToggle={toggleVendor}
          onClear={() => setSelectedVendors(new Set())}
          label={t('modelSelect.vendorFilter')}
          clearLabel={t('modelSelect.clearFilters')}
        />
      )}
      {allTags.length > 0 && (
        <TagFilter
          tags={allTags}
          selected={selectedTags}
          onToggle={toggleTag}
          onClear={() => setSelectedTags(new Set())}
          label={t('modelSelect.tagsFilter')}
          clearLabel={t('modelSelect.clearFilters')}
        />
      )}
      <SearchBox value={searchQuery} onChange={setSearchQuery} placeholder={t('modelSelect.searchPlaceholder')} />
      {filterActive && (
        <div className="px-3 pb-1 text-xs text-muted-foreground">
          {t('modelSelect.countShown', { shown: String(filtered.length), total: String(afterCapFilter.length) })}
        </div>
      )}
    </>
  )

  // Modal variant: trigger + dialog overlay
  if (variant === 'modal') {
    // Loading/error: render a disabled trigger button so the modal entry-point is always visible.
    if (isLoading || error) {
      return (
        <button
          type="button"
          disabled
          aria-label="Select model"
          aria-haspopup="dialog"
          className={cn(
            'inline-flex items-center gap-2 border border-border bg-card text-foreground',
            'rounded-lg px-3 py-2 text-sm',
            'opacity-60 cursor-not-allowed',
            className,
          )}
        >
          <span className="text-muted-foreground">
            {isLoading ? t('modelSelect.loading') : t('modelSelect.loadFailed')}
          </span>
          <ChevronDown size={14} className="text-muted-foreground ml-auto" aria-hidden />
        </button>
      )
    }

    return (
      <>
        <SelectedSummary
          model={selectedModel}
          isEmptySelected={allowEmpty && value === ''}
          ariaLabel={t('modelSelect.selectTitle')}
          emptyLabel={resolvedEmptyLabel}
          selectLabel={t('modelSelect.selectModel')}
          reselectLabel={t('modelSelect.reselect')}
          disabled={disabled}
          open={open}
          ariaHasPopup="dialog"
          onOpen={() => setOpen(true)}
          className={className}
        />

        {open && (
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={labelId}
            className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/20"
            onClick={(e) => {
              if (e.target === e.currentTarget) { setOpen(false); setSearchQuery('') }
            }}
          >
            <div
              className={cn(
                'bg-card border border-border rounded-xl shadow-elevation-3',
                'w-full max-w-lg max-h-[70vh] flex flex-col overflow-hidden',
              )}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
                <span id={labelId} className="font-semibold text-foreground">{t('modelSelect.selectTitle')}</span>
                <button
                  type="button"
                  onClick={() => { setOpen(false); setSearchQuery('') }}
                  aria-label={t('modelSelect.closeLabel')}
                  className="text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-ring rounded"
                >
                  <X size={16} aria-hidden />
                </button>
              </div>
              <div className="border-b border-border shrink-0">
                {controls}
              </div>
              <div className="overflow-y-auto flex-1">
                {listBody}
              </div>
            </div>
          </div>
        )}
      </>
    )
  }

  // Inline / compact variants: loading / error inline box
  if (isLoading) {
    return (
      <div
        className={cn(
          'border border-border bg-card text-foreground rounded-lg px-3 py-3',
          className,
        )}
      >
        <span className="text-sm text-muted-foreground">{t('modelSelect.loading')}</span>
      </div>
    )
  }

  if (error) {
    return (
      <div
        className={cn(
          'border border-border bg-card text-foreground rounded-lg px-3 py-3',
          className,
        )}
      >
        <span className="text-sm text-destructive">{t('modelSelect.loadFailed')}</span>
      </div>
    )
  }

  // Inline / compact variants: collapsed locked summary, or the expanded picker.
  if (!open) {
    return (
      <SelectedSummary
        model={selectedModel}
        isEmptySelected={allowEmpty && value === ''}
        ariaLabel={t('modelSelect.selectTitle')}
        emptyLabel={resolvedEmptyLabel}
        selectLabel={t('modelSelect.selectModel')}
        reselectLabel={t('modelSelect.reselect')}
        disabled={disabled}
        open={open}
        ariaHasPopup="listbox"
        onOpen={() => setOpen(true)}
        className={className}
      />
    )
  }

  return (
    <div
      className={cn(modelSelectVariants({ variant }), disabled && 'opacity-60 pointer-events-none', className)}
      aria-disabled={disabled}
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-border">
        <span className="font-semibold text-sm text-foreground">{t('modelSelect.selectTitle')}</span>
        <button
          type="button"
          onClick={() => { setOpen(false); setSearchQuery('') }}
          aria-label={t('modelSelect.closeLabel')}
          className="text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-ring rounded"
        >
          <X size={16} aria-hidden />
        </button>
      </div>
      <div className="border-b border-border">
        {controls}
      </div>
      <div className={cn(variant === 'compact' ? 'max-h-72' : 'max-h-96', 'overflow-y-auto')}>
        {listBody}
      </div>
    </div>
  )
}
